import { useState } from 'react';

export type InfoSectionId = 'about' | 'privacy' | 'cookies';

interface InfoPageProps {
  section: InfoSectionId;
}

interface InfoItem {
  title: string;
  body: string;
}

interface InfoSection {
  eyebrow: string;
  title: string;
  summary: string;
  items: InfoItem[];
}

const contactEmailUser = ['retro', 'jet'] as const;
const contactEmailDomain = ['ksionek', 'com'] as const;

const infoSections: Record<InfoSectionId, InfoSection> = {
  about: {
    eyebrow: 'Project info',
    title: 'About RetroJet',
    summary: 'RetroJet is a hobby project built for simple, collaborative retrospectives.',
    items: [
      {
        title: 'Free to use',
        body: 'The app is currently free and does not apply usage limits. Feedback and improvement ideas are welcome.',
      },
      {
        title: 'No warranty',
        body: 'RetroJet is provided as-is, without any availability, data retention, or fitness-for-purpose guarantees. You use it at your own responsibility.',
      },
    ],
  },
  privacy: {
    eyebrow: 'Privacy',
    title: 'Privacy',
    summary: 'This is a plain-language overview of how RetroJet handles basic app data.',
    items: [
      {
        title: 'What is stored',
        body: 'RetroJet stores board content, columns, cards, votes, participant names, user-board links, and feedback you submit through the app.',
      },
      {
        title: 'Where data is stored',
        body: 'The production infrastructure is configured for AWS us-east-1, so application data is stored in the United States. Static frontend files are delivered through AWS CloudFront.',
      },
      {
        title: 'Local browser data',
        body: 'Your browser may keep local settings such as theme preference, a temporary user identifier, display name, and sign-in session details.',
      },
      {
        title: 'Access and retention',
        body: 'Board links can be shared with other people, so treat board content as shared workspace content. Data may be retained while the service is running unless it is removed by an admin flow.',
      },
    ],
  },
  cookies: {
    eyebrow: 'Cookies',
    title: 'Cookies',
    summary: 'RetroJet keeps cookie usage minimal and uses browser storage for app preferences.',
    items: [
      {
        title: 'No tracking cookies',
        body: 'RetroJet does not use tracking or advertising cookies for the core app experience.',
      },
      {
        title: 'Browser storage',
        body: 'The app uses local browser storage for small values such as theme preference, user identity, display name, and optional session data.',
      },
      {
        title: 'Third-party sign-in',
        body: 'If Google sign-in is enabled and you choose to use it, Google may use its own cookies or browser storage according to Google account settings.',
      },
    ],
  },
};

function getContactEmail(): string {
  return `${contactEmailUser.join('')}@${contactEmailDomain.join('.')}`;
}

function getVisibleContactEmail(): string {
  return `${contactEmailUser.join('')} [at] ${contactEmailDomain[0]} [dot] ${contactEmailDomain[1]}`;
}

function ContactPanel(): JSX.Element {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleCopy = async (): Promise<void> => {
    if (!navigator.clipboard) {
      setCopyState('failed');
      return;
    }

    try {
      await navigator.clipboard.writeText(getContactEmail());
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2400);
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <section className="mt-8 rounded-lg border border-blue-100 bg-blue-50 p-5 dark:border-blue-900/60 dark:bg-blue-950/40">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contact</h2>
      <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
        Send feedback, bug reports, and improvement ideas to{' '}
        <span className="font-medium text-gray-900 dark:text-white">
          {getVisibleContactEmail()}
        </span>
        .
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => {
            void handleCopy();
          }}
          className="btn-secondary text-sm"
        >
          Copy email
        </button>
        {copyState === 'copied' && (
          <span className="text-sm font-medium text-green-700 dark:text-green-300">
            Email copied
          </span>
        )}
        {copyState === 'failed' && (
          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
            Copy unavailable
          </span>
        )}
      </div>
    </section>
  );
}

export function InfoPage({ section }: InfoPageProps): JSX.Element {
  const content = infoSections[section];

  return (
    <section className="min-h-[calc(100vh-8rem)] bg-gray-50 px-4 py-12 dark:bg-gray-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <p className="text-sm font-semibold uppercase text-blue-700 dark:text-blue-300">
          {content.eyebrow}
        </p>
        <h1 className="mt-3 text-4xl font-extrabold text-gray-900 dark:text-white">
          {content.title}
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-8 text-gray-600 dark:text-gray-300">
          {content.summary}
        </p>

        <div className="mt-8 grid gap-4">
          {content.items.map((item) => (
            <article
              key={item.title}
              className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900"
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {item.title}
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
                {item.body}
              </p>
            </article>
          ))}
        </div>

        {section === 'about' && <ContactPanel />}
      </div>
    </section>
  );
}
